import type { BookingPageErrorState, RequestState } from 'bookwithme-shared';
import type { OwaDate } from 'owa-datetime';

import type { BookMeetingStatus } from '../../services/schema/BookMeetingResponse';
import type { EmailAddress } from '../../services/schema/CheckBookableResponse';
import type { AlternateAccount } from './AlternateAccount';
import type { BookableTimeSlot } from './BookableTimeSlot';
import type { BookabilityDataModel, BookingService } from './BookingService';
import type { CustomerInfo, CustomerInfoNeeded } from './CustomerInfo';
import type { Meeting } from './Meeting';
import type { PersonalBookingData } from './PersonalBookingData';
import type { ShowBookingModal } from './ShowBookingModal';
import type { TimeSuggestionFilters } from './TimeSuggestionFilters';
import type { TimeSuggestionsRequestState } from './TimeSuggestionsRequestState';
import type { AttendeeInfo } from './AttendeeInfo';
import type { CustomQuestion } from 'bookings-shared/lib/c2/services/schema/customQuestion';

export const COOKIE_REFRESH = 'cookieRefresh';

export interface BookingStore {
    // Booking mailbox
    bookingData: PersonalBookingData;
    bookingMailbox: string | null;
    obscuredBookingEmailAddress: string; //Obscured smtp address
    isPrivate: boolean; //to check for private service
    bookingSignedUrl: string;
    isSingleUseLink: boolean;

    // Availability, timeslots, and time suggestions
    bookingAvailability: BookabilityDataModel;
    displayTimeZone: string;
    bookableTimeSlots: Map<number, BookableTimeSlot | null>;
    showTimeSuggestions: boolean;

    // Time suggestion slots that are displayed in the time suggestion section
    timeSuggestions: BookableTimeSlot[] | null;
    earliestTimeAvailableToBook: OwaDate;
    maxDateAllowedForBooking: OwaDate;
    fetchedMonths: number[];

    // Data needed for booking
    customerInfo: CustomerInfo;
    customerInfoNeeded: CustomerInfoNeeded;
    emailVerificationNeeded: boolean;
    emailVerificationCode: string | null;
    selectedDate: OwaDate;
    selectedMeeting: Meeting;
    restrictedDates: Date[];
    weekStartDay: number;

    //timeSuggestionMap is mapping all available time slots to dates
    timeSuggestionMap: Map<string, BookableTimeSlot[]>;
    selectedDateTimeSuggestions: BookableTimeSlot[];
    showBookMeetingModal: ShowBookingModal;
    bookMeetingStatus: BookMeetingStatus;
    timeSuggestionFilters: TimeSuggestionFilters;

    // Authentication & accounts
    alternateAccounts: AlternateAccount[];
    // Flag for whether the user has selected an account or to continue as a guest - true in both cases
    accountSelectedOrGuest: boolean;
    // Flag for whether the user specifically selected to continue with an account
    isAccountSelected: boolean;
    // Flag for whether the user needs to verify their identity before viewing the booking page
    isAuthRequiredToAccessBookingPage: boolean;
    // Flag for whether the user selected email verification to verify their identity
    isEmailVerificationSelected: boolean;
    tempAllowedAccounts: Array<EmailAddress>;
    // Flag for whether the user has access to the booking through DL membership
    attendeeHasAccessThroughGroupOrDL: boolean;

    // Request states
    initialRequestState: RequestState;
    timeSuggestionsRequestState: TimeSuggestionsRequestState;
    bookingPageErrorState: BookingPageErrorState;
    editBooking: boolean;
    urlBookingId: string;
    currentBookingService: BookingService | null;
    themeImage: string;
    currentMeetingTypeId: string;
    entryPoint: string;
    wasPrimed: boolean;

    additionalAttendees: AttendeeInfo[];
    bookingMailboxSmtpAddress: string;

    customQuestions: CustomQuestion[];

    xBEServer: string;
}
